import { FC, useId } from "react";
import { Box } from "@mui/material";
import { Swiper, SwiperProps } from "swiper/react";
import { Mousewheel, Scrollbar } from "swiper/modules";
import "swiper/css";
import "swiper/css/scrollbar";

import { Colors } from "../../../../configs/colors";

/**
 * Paged POS menu (services, garments, styles, fabrics, colours, customizations).
 * On top of touch/mouse dragging it pages with the mouse wheel / trackpad and
 * has a draggable scrollbar when there's more than one page. The bar stays
 * hidden: it shows while the menu is being scrolled and while a mouse hovers
 * the menu, then fades out. It sits in the gap under the menu, so layouts
 * don't shift.
 */
const PosSwiper: FC<SwiperProps> = ({ children, style, modules = [], ...props }) => {
  // useId gives ":r1:"; keep it a valid CSS selector
  const barId = `pos-swiper-bar-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;

  return (
    <Box
      sx={{
        position: "relative",
        width: style?.width,
        flexShrink: 0,
        // Swiper fades the bar out with an inline opacity; hovering brings it
        // back (mouse only - touch shows it while swiping)
        "@media (hover: hover)": {
          [`&:hover > #${barId}`]: { opacity: "1 !important", transitionDuration: "150ms !important" },
        },
      }}
    >
      <Box
        id={barId}
        data-testid="pos-swiper-scrollbar"
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: "-0.79rem",
          height: "0.43rem",
          borderRadius: "0.21rem",
          background: "rgba(0, 0, 0, 0.08)",
          zIndex: 1,
          cursor: "pointer",
          "& .swiper-scrollbar-drag": {
            position: "relative",
            height: "100%",
            borderRadius: "0.21rem",
            background: Colors.MAIN_GRAY,
            opacity: 0.55,
            cursor: "grab",
            transition: "opacity 120ms",
            // Bigger grab area than the thin bar, for touch
            "&::after": { content: '""', position: "absolute", inset: "-0.57rem 0" },
          },
          "&:hover .swiper-scrollbar-drag": { opacity: 0.85 },
          "&.swiper-scrollbar-lock": { display: "none" },
        }}
      />
      <Swiper
        {...props}
        style={style}
        modules={[Mousewheel, Scrollbar, ...modules]}
        // A wheel notch / trackpad swipe flips one page; at either end the
        // page itself scrolls again
        mousewheel={{ forceToAxis: false, releaseOnEdges: true, thresholdDelta: 10 }}
        scrollbar={{ el: `#${barId}`, draggable: true, snapOnRelease: true, hide: true }}
      >
        {children}
      </Swiper>
    </Box>
  );
};

export default PosSwiper;
